import { Sheet } from 'geniebook-ui/dist';
import { useAtom } from 'jotai';
import React, { useRef, useEffect } from 'react';
import {
  examFilterOptionListAtom,
  extendedFilterAtom,
  levelFilterOptionListAtom,
  selectedFilterOptionAtom,
  showAllFilterAtom,
  showFilterSheetAtom,
  subjectFilterOptionListAtom,
} from '@atom/examResultAtom';
import FilterList from './FilterList';

export type filterIdType = {
  id: 'subject' | 'level' | 'type';
};

export type selectedOptionProps = {
  id: 'subject' | 'level' | 'type';
  item: string | number;
};
interface SheetWrapperProps {
  onClickOutside: (target: EventTarget | null) => void;
  children: React.ReactNode;
  excludeSelector?: string;
}

const FilterResultOnMobileAndTablet = () => {
  const [showFilterExamResult, setShowFilterExamResult] =
    useAtom(showFilterSheetAtom);
  const [showAllFilter, setShowAllFilter] = useAtom(showAllFilterAtom);
  const [extendedFilter] = useAtom(extendedFilterAtom);

  // option list
  const [examFilterOptionList] = useAtom(examFilterOptionListAtom);
  const [subjectFilterOptionList] = useAtom(subjectFilterOptionListAtom);
  const [levelFilterOptionList] = useAtom(levelFilterOptionListAtom);

  const [, setSelectedOption] = useAtom(selectedFilterOptionAtom);
  const handleSelected = (props: selectedOptionProps) => {
    const { id, item } = props;
    setSelectedOption(prevState => ({
      ...prevState,
      [id]: item.toString().trim(),
    }));
  };

  const handleOutsideClick = () => {
    if (!showAllFilter) {
      setShowFilterExamResult(false);
    } else {
      setShowAllFilter(!showAllFilter);
    }
  };

  const toggleFilterExamResult = () => {
    setShowFilterExamResult(!showFilterExamResult);
  };

  return (
    <>
      <Sheet
        isOpen={showFilterExamResult}
        toggleSheet={() => toggleFilterExamResult()}
        zIndex={22}
        darkMode={false}
        className="exclude-element [&>div>.shadow-div]:h-[20px]"
        closeHeight="0%"
      >
        <SheetWrapper
          onClickOutside={handleOutsideClick}
          excludeSelector=".exclude-element"
        >
          <div className="flex flex-col gap-y-5 w-full max-h-[80vh] overflow-auto">
            <FilterList
              id="subject"
              titleList="Subject"
              list={subjectFilterOptionList}
              onClick={handleSelected}
            />
            <FilterList
              id="level"
              titleList="Level"
              list={levelFilterOptionList}
              onClick={handleSelected}
            />
            <FilterList
              id="type"
              titleList="Type"
              list={examFilterOptionList}
              onClick={handleSelected}
            />
            <div className={'h-[64px]'} />
          </div>
        </SheetWrapper>
      </Sheet>

      {/* extended sheet. a sheet that will appear when clicking the "Show all" text link */}
      {extendedFilter && (
        <Sheet
          isOpen={showAllFilter}
          toggleSheet={() => setShowAllFilter(!showAllFilter)}
          zIndex={22}
          darkMode={false}
          className={`exclude-element`}
          closeHeight="0%"
        >
          <SheetWrapper
            onClickOutside={handleOutsideClick}
            excludeSelector=".exclude-element"
          >
            <div className="flex flex-col gap-y-5 w-full max-h-[80vh] overflow-auto">
              <FilterList
                id={extendedFilter.id}
                titleList={extendedFilter.titleList}
                list={extendedFilter.list}
                inExtendedSheet={true}
                onClick={handleSelected}
              />
              <div className={'h-[64px]'} />
            </div>
          </SheetWrapper>
        </Sheet>
      )}
    </>
  );
};

// to allow close the sheet on click outside
const SheetWrapper: React.FC<SheetWrapperProps> = ({
  onClickOutside,
  children,
  excludeSelector,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target || !ref.current || ref.current.contains(target)) return;

      if (excludeSelector) {
        const excludedElements = document.querySelectorAll(excludeSelector);
        const excludedArray = Array.from(excludedElements); // convert NodeList to Array
        for (const excludedElement of excludedArray) {
          if (excludedElement.contains(target)) return;
        }
      }

      onClickOutside(target);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClickOutside, excludeSelector]);

  return (
    <div id="sheetWrapper" ref={ref}>
      {children}
    </div>
  );
};

export default FilterResultOnMobileAndTablet;
